import React from "react";
import Layout from "../components/Layout";
import MetaTags from "../lib/MetaTags";
import Breadcrumbs from "../components/Breadcrumbs";
import { SITE } from "../lib/categories";
import { ExternalLink, MapPin } from "lucide-react";

const PANELS = [
  {
    id: "northwest-side",
    title: "Northwest Side — Repair & Buyback",
    note: "Independent device repair and cash-buyback options serving Irving Park, Avondale, Portage Park and nearby areas.",
    entries: [
      {
        name: "2A Electronics",
        url: "https://2aelectronics.com",
        featured: true,
        desc: "Same-day phone, tablet, laptop and game-console repair, instant cash buyback for working or broken devices, certified refurbished sales, and nationwide mail-in repair.",
      },
      {
        name: "PCs for People (refurbished + recycling)",
        url: "https://www.pcsforpeople.org",
        desc: "Nonprofit that refurbishes donated computers for low-income households and responsibly recycles what it cannot reuse.",
      },
    ],
  },
  {
    id: "city-programs",
    title: "Citywide Recycling Drop-Off & Government Programs",
    note: "Official municipal and state programs for safe electronics and e-waste disposal in Chicagoland.",
    entries: [
      {
        name: "City of Chicago — Electronics Recycling",
        url: "https://www.chicago.gov",
        desc: "City drop-off facilities and household chemicals & computer recycling resources for Chicago residents.",
      },
      {
        name: "Cook County — Sustainability & E-Waste Events",
        url: "https://www.cookcountyil.gov",
        desc: "Suburban Cook County electronics recycling events and permanent collection information.",
      },
      {
        name: "Illinois EPA — Electronics Recycling",
        url: "https://epa.illinois.gov",
        desc: "Statewide guidance on the Illinois electronics landfill ban and approved recyclers.",
      },
    ],
  },
  {
    id: "manufacturer-trade-in",
    title: "Manufacturer & Trade-In Programs",
    note: "First-party trade-in and takeback programs from device makers.",
    entries: [
      {
        name: "Apple Trade In",
        url: "https://www.apple.com/shop/trade-in",
        desc: "Trade in eligible Apple devices for credit, or recycle them for free if they have no trade-in value.",
      },
      {
        name: "Samsung Trade-In",
        url: "https://www.samsung.com/us/trade-in/",
        desc: "Trade-in credit toward new Samsung devices and free responsible recycling of old electronics.",
      },
    ],
  },
  {
    id: "donation-reuse",
    title: "Donation & Reuse Nonprofits",
    note: "Give working devices a second life or recycle responsibly through established charities.",
    entries: [
      {
        name: "The Salvation Army",
        url: "https://satruck.org",
        desc: "Accepts donated working electronics that fund community rehabilitation programs.",
      },
      {
        name: "US EPA — Electronics Donation & Recycling",
        url: "https://www.epa.gov/recycle/electronics-donation-and-recycling",
        desc: "Federal directory of certified recyclers and manufacturer takeback programs.",
      },
    ],
  },
];

export default function Directory() {
  const pageUrl = `${SITE.url}/directory`;
  const allEntries = PANELS.flatMap((p) => p.entries);
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "The Ultimate Chicago Electronics Recycling & Device Repair Directory",
    description:
      "An independent directory of Chicago electronics recycling drop-offs, device repair shops, buyback options, manufacturer trade-in programs, and donation nonprofits.",
    url: pageUrl,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: allEntries.map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: e.name,
        url: e.url,
      })),
    },
  };

  return (
    <Layout>
      <MetaTags
        title="The Ultimate Chicago Electronics Recycling & Device Repair Directory"
        description="Independent directory of Chicago electronics recycling drop-offs, device repair shops, buyback options, manufacturer trade-in programs, and donation nonprofits."
        url={pageUrl}
        schema={schema}
      />

      <section className="bg-white border-b border-[#121212]">
        <div className="ceg-container py-12">
          <Breadcrumbs items={[{ to: "/", label: "Home" }, { label: "Directory" }]} />
          <p className="ceg-overline mt-6">Resource Hub · Updated for 2026</p>
          <h1 className="font-['Outfit'] font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05] max-w-4xl">
            The Ultimate Chicago Electronics Recycling & Device Repair Directory
          </h1>
          <p className="mt-5 text-base sm:text-lg md:text-xl text-[#5C5C5C] max-w-3xl leading-relaxed">
            A neutral, independent directory of where to repair, sell, donate, or responsibly recycle phones, laptops, tablets, and gaming consoles across Chicagoland. We do not accept payment for placement, and listings are organized to help you find the right option fast.
          </p>
        </div>
      </section>

      <section className="ceg-container py-12 grid grid-cols-12 gap-8" data-testid="directory-grid">
        {PANELS.map((panel) => (
          <div key={panel.id} className="col-span-12 lg:col-span-6" data-testid={`directory-panel-${panel.id}`}>
            <div className="border border-[#121212] bg-white h-full flex flex-col">
              <div className="bg-[#121212] text-white px-5 py-4">
                <p className="ceg-overline text-white flex items-center gap-2"><MapPin size={12} /> {panel.title}</p>
              </div>
              <div className="p-5 flex-1">
                <p className="text-sm text-[#5C5C5C] mb-4">{panel.note}</p>
                <ul className="space-y-4">
                  {panel.entries.map((e) => (
                    <li
                      key={e.name}
                      className={`border ${e.featured ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-[#E5E5E5]"} p-4`}
                      data-testid={`directory-entry-${e.url}`}
                    >
                      {e.featured && (
                        <span className="block w-fit bg-[#FF3823] text-white text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold mb-2">
                          Featured · Northwest Side
                        </span>
                      )}
                      <a
                        href={e.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-['Outfit'] font-bold text-lg tracking-tight hover:text-[#0055FF] inline-flex items-center gap-1"
                        data-testid={`directory-link-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                      >
                        {e.name} <ExternalLink size={13} className="shrink-0" />
                      </a>
                      <p className="text-sm text-[#5C5C5C] mt-2 leading-relaxed">{e.desc}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-white border-t border-[#121212]">
        <div className="ceg-container py-10">
          <p className="text-xs text-[#5C5C5C] max-w-3xl leading-relaxed">
            Listings are provided for informational purposes and are not paid placements. Hours, programs, and accepted items change — always confirm details directly with each organization before visiting. Removed or outdated a listing? Let us know via the <a href="/contact" className="underline underline-offset-4 decoration-[#0055FF]">contact page</a>.
          </p>
        </div>
      </section>
    </Layout>
  );
}
